import { useState } from 'react';
import type { FormEvent } from 'react';
import { Alert, Box, Grid, MenuItem, Stack, TextField } from '@mui/material';
import { PrimaryButton } from '@/components/PrimaryButton';
import { findCustomerByEmail } from '@/features/customers/api';
import {
  customerFieldMessages,
  fieldErrorsFrom,
  formatPhone,
  isValidEmail,
  normalizePhone,
} from '@/features/customers/customerForm';
import { CounterAccountLookup } from '@/features/customers/components/CounterAccountLookup';
import {
  isLookupSettled,
  type AccountLookup,
  type CounterContactErrors,
  type CounterContactValues,
} from '@/features/customers/counterLookup';
import { createInternalQuotation } from '@/features/operations/api';
import type {
  CreatedInternalQuotation,
  DeclaredAnswerInput,
  PhoneModelOption,
  PublicDeclaredField,
} from '@/features/operations/types';

/**
 * Counter intake. The same data the public form collects, because it produces the same
 * request: contact, a catalogue model with one of its capacities, and the declared fields.
 *
 * `answers` is keyed by field id and holds strings for both field types -- an option id for a
 * choice, the typed digits for a percentage -- because that is what the inputs produce. It is
 * converted at submit, the same way the public form does it.
 */
type QuotationIntakeFormState = {
  fullName: string;
  email: string;
  phone: string;
  phoneModelId: string;
  storageGb: string;
  answers: Record<string, string>;
};

const defaultQuotationIntakeForm: QuotationIntakeFormState = {
  fullName: '',
  email: '',
  phone: '',
  phoneModelId: '',
  storageGb: '',
  answers: {},
};

export function ManualQuotationForm({
  token,
  phoneModels,
  declaredFields,
  onCreated,
}: {
  token: string;
  phoneModels: PhoneModelOption[];
  declaredFields: PublicDeclaredField[];
  onCreated: (quotation: CreatedInternalQuotation) => void;
}) {
  const [intakeForm, setIntakeForm] = useState<QuotationIntakeFormState>(
    defaultQuotationIntakeForm,
  );
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lookup, setLookup] = useState<AccountLookup>({ status: 'idle' });
  const [contactErrors, setContactErrors] = useState<CounterContactErrors>({});

  const setContactField = (field: keyof CounterContactValues, value: string) => {
    setIntakeForm((current) => {
      const next = { ...current, [field]: value };

      // Another mail is another person: the name and phone the previous account filled in go
      // away with it, unless the employee already changed them (#229, U-1).
      if (field === 'email' && lookup.status === 'found') {
        const prefilledPhone = lookup.customer.phone ? formatPhone(lookup.customer.phone) : '';

        if (current.fullName === lookup.customer.fullName) {
          next.fullName = '';
        }

        if (current.phone === prefilledPhone) {
          next.phone = '';
        }
      }

      return next;
    });
    setContactErrors((current) => ({ ...current, [field]: undefined }));

    if (field === 'email' && lookup.status !== 'idle') {
      setLookup({ status: 'idle' });
    }
  };

  /**
   * Looks for the account of the typed mail. Found, its name and phone fill the fields, for the
   * employee to confirm with the person; what they confirm is what the quotation records.
   */
  const lookUpAccount = async () => {
    const email = intakeForm.email.trim().toLowerCase();

    if (!email) {
      setContactErrors((current) => ({ ...current, email: customerFieldMessages.emailRequired }));
      return;
    }

    if (!isValidEmail(email)) {
      setContactErrors((current) => ({ ...current, email: customerFieldMessages.emailInvalid }));
      return;
    }

    setLookup({ status: 'searching', email });

    try {
      const customer = await findCustomerByEmail(token, email);

      if (customer) {
        setLookup({ status: 'found', email, customer });
        setIntakeForm((current) => ({
          ...current,
          fullName: customer.fullName,
          phone: customer.phone ? formatPhone(customer.phone) : '',
        }));
      } else {
        setLookup({ status: 'none', email });
      }
    } catch {
      setLookup({ status: 'failed', email });
    }
  };

  /// The contact, field by field, with the same checks the backend runs.
  const describeContactProblems = (): CounterContactErrors => {
    const problems: CounterContactErrors = {};

    if (!intakeForm.fullName.trim()) {
      problems.fullName = 'Cargá el nombre de quien ofrece el equipo.';
    }

    if (!intakeForm.phone.trim()) {
      problems.phone = 'Cargá el teléfono de quien ofrece el equipo.';
    } else if (!normalizePhone(intakeForm.phone)) {
      problems.phone = customerFieldMessages.phoneInvalid;
    }

    return problems;
  };

  const selectedIntakeModel = phoneModels.find((model) => model.id === intakeForm.phoneModelId);

  /**
   * The same checks the backend runs, so the person at the counter finds out before the round
   * trip. The backend runs them regardless: a form is not what makes an answer valid (BR-063).
   */
  const describeIntakeProblem = (): string | null => {
    if (!intakeForm.phoneModelId || !intakeForm.storageGb) {
      return 'Elegí el modelo y la capacidad del equipo.';
    }

    const missing = declaredFields.filter(
      (field) => field.isRequired && !(intakeForm.answers[field.id] || '').trim(),
    );

    if (missing.length) {
      return `Completá ${missing.map((field) => field.label.toLowerCase()).join(', ')}.`;
    }

    const outOfRange = declaredFields.find((field) => {
      if (field.type !== 'PERCENTAGE') {
        return false;
      }

      const raw = (intakeForm.answers[field.id] || '').trim();

      if (!raw) {
        return false;
      }

      const value = Number(raw);
      return !Number.isInteger(value) || value < 0 || value > 100;
    });

    if (outOfRange) {
      return `${outOfRange.label} tiene que ser un número entero entre 0 y 100.`;
    }

    return null;
  };

  const buildIntakeAnswers = (): DeclaredAnswerInput[] =>
    declaredFields
      .filter((field) => (intakeForm.answers[field.id] || '').trim())
      .map((field) =>
        field.type === 'SINGLE_CHOICE'
          ? { fieldId: field.id, optionId: intakeForm.answers[field.id] }
          : { fieldId: field.id, percentValue: Number(intakeForm.answers[field.id]) },
      );

  const handleCreateQuotation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // The account is looked up before anything is registered, so the employee always sees
    // whether the person already had one.
    if (!isLookupSettled(lookup)) {
      void lookUpAccount();
      return;
    }

    const contactProblems = describeContactProblems();
    const problem = describeIntakeProblem();
    setContactErrors(contactProblems);
    setError(problem);

    if (Object.keys(contactProblems).length || problem) {
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const createdQuotation = await createInternalQuotation(token, {
        fullName: intakeForm.fullName.trim(),
        email: intakeForm.email.trim(),
        phone: intakeForm.phone.trim(),
        phoneModelId: intakeForm.phoneModelId,
        storageGb: Number(intakeForm.storageGb),
        answers: buildIntakeAnswers(),
      });

      onCreated(createdQuotation);
    } catch (cause) {
      // A mail of staff or of a deactivated account: the backend marks it on the mail field.
      const markedEmail = fieldErrorsFrom(cause)?.email;

      if (markedEmail) {
        setContactErrors((current) => ({ ...current, email: markedEmail }));
      } else {
        setError(cause instanceof Error ? cause.message : 'No pudimos registrar la cotización.');
      }

      setIsCreating(false);
    }
  };

  return (
    <Stack component="form" spacing={2.5} noValidate onSubmit={handleCreateQuotation}>
      {phoneModels.length === 0 ? (
        <Alert severity="warning">
          No hay modelos en el catálogo, así que todavía no se puede registrar una cotización.
          Pedile a una administradora que los cargue.
        </Alert>
      ) : null}

      <CounterAccountLookup
        values={{
          fullName: intakeForm.fullName,
          email: intakeForm.email,
          phone: intakeForm.phone,
        }}
        errors={contactErrors}
        lookup={lookup}
        onChange={setContactField}
        onLookup={() => void lookUpAccount()}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {/*
            Chosen from the catalogue, not typed. The counter has the same constraint the public
            form has: a request names a catalogue entry, which is what the pricing resolves
            against and what keeps "aple" out of the data (ADR 0002). A phone PhoneTec has not
            loaded cannot be quoted here either.
          */}
          <TextField
            select
            fullWidth
            required
            label="Modelo"
            value={intakeForm.phoneModelId}
            onChange={(event) =>
              // The capacity belongs to the model, so keeping the previous one selected would
              // offer a capacity this phone may not come in.
              setIntakeForm((current) => ({
                ...current,
                phoneModelId: event.target.value,
                storageGb: '',
              }))
            }
          >
            {phoneModels.map((model) => (
              <MenuItem key={model.id} value={model.id}>
                {model.brand} {model.model}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            fullWidth
            required
            label="Capacidad"
            value={intakeForm.storageGb}
            disabled={!selectedIntakeModel}
            helperText={selectedIntakeModel ? undefined : 'Elegí primero el modelo.'}
            onChange={(event) =>
              setIntakeForm((current) => ({ ...current, storageGb: event.target.value }))
            }
          >
            {(selectedIntakeModel?.storageOptions || []).map((option) => (
              <MenuItem key={option} value={String(option)}>
                {option}GB
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {/*
        The questions come from the configuration, exactly like the public form. There is no
        list of fields in this file: what gets asked at the counter and what gets asked on the
        site are the same rows, so the two cannot drift apart.
      */}
      {declaredFields.length === 0 ? (
        <Alert severity="error">
          No se pudieron cargar las características fijas de la cotización. Intentá nuevamente antes
          de registrar la solicitud.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {declaredFields.map((field) => (
            <Grid item xs={12} md={4} key={field.id}>
              <TextField
                select={field.type === 'SINGLE_CHOICE'}
                type={field.type === 'PERCENTAGE' ? 'number' : undefined}
                fullWidth
                required={field.isRequired}
                label={field.label}
                value={intakeForm.answers[field.id] || ''}
                helperText={field.helpText || undefined}
                inputProps={field.type === 'PERCENTAGE' ? { min: 0, max: 100, step: 1 } : undefined}
                onChange={(event) =>
                  setIntakeForm((current) => ({
                    ...current,
                    answers: { ...current.answers, [field.id]: event.target.value },
                  }))
                }
              >
                {field.type === 'SINGLE_CHOICE'
                  ? field.options.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                      </MenuItem>
                    ))
                  : null}
              </TextField>
            </Grid>
          ))}
        </Grid>
      )}

      {/*
        BR-012, said to the person who is about to say a number out loud to somebody standing in
        front of them. The web disclaimer is aimed at the applicant; this one is aimed at
        whoever is attending.
      */}
      <Alert severity="info">
        El monto es estimado y no es una oferta cerrada: sale de lo que la persona declara, y el
        precio final se define recién después de la revisión técnica.
      </Alert>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box>
        <PrimaryButton type="submit" disabled={isCreating || phoneModels.length === 0}>
          {isCreating ? 'Registrando...' : 'Registrar cotización'}
        </PrimaryButton>
      </Box>
    </Stack>
  );
}
